/**
 * Cross-item review passes — checks that only make sense over the WHOLE
 * paste, run after per-item validation: mergeable same-action twins, and
 * rotation verbs the client explicitly opted out of. Warnings only; they
 * never flip an item's accepted state.
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  LibraryValidationContext,
  ReviewIssue,
  ReviewItem,
} from '../review-items';

/** Rule groups on one layer sharing one action set belong in ONE group with
 *  OR-joined conditions — models love splitting them; flag every twin.
 *  Weight groups are exempt (their payloads differ meaningfully). */
export function flagMergeableRuleGroups(items: ReviewItem[]): ReviewItem[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const signature = signatureOf(item);
    if (signature) counts.set(signature, (counts.get(signature) ?? 0) + 1);
  }
  return items.map((item) => {
    const signature = signatureOf(item);
    if (!signature || (counts.get(signature) ?? 0) < 2) return item;
    const issue: ReviewIssue = {
      level: 'warning',
      message:
        'Same layer + same action as another rule — merge them into ONE rule with OR-joined conditions.',
    };
    return { ...item, issues: [...item.issues, issue] };
  });
}

function signatureOf(item: ReviewItem): string | null {
  const group = item.ruleGroup;
  if (!group || group.kind !== 'group') return null;
  const actions = group.actions ?? [];
  if (!actions.length || actions.includes('weight')) return null;
  return `${item.layer}|${[...actions].sort().join('+')}`;
}

const ROTATION_VERBS = [
  'set-to-queue',
  'set-to-waiting',
  'eliminate',
  'add-element',
];

/** The client asked for NO rotation — flag any rule that still swaps. */
export function flagRotationMismatch(
  items: ReviewItem[],
  ctx: LibraryValidationContext,
): ReviewItem[] {
  if (ctx.ruleStyle !== 'in-place') return items;
  return items.map((item) => {
    const group = item.ruleGroup;
    if (!group || group.kind !== 'group') return item;
    const verbs = (group.actions ?? []).filter((action) =>
      ROTATION_VERBS.includes(action),
    );
    if (!verbs.length) return item;
    const issue: ReviewIssue = {
      level: 'warning',
      message: `You chose "adjust in place" — this rule rotates (${verbs.join(', ')}).`,
    };
    return { ...item, issues: [...item.issues, issue] };
  });
}
